import { useMemo } from "react";
import { ArrowDown, ArrowRight, ArrowUp, CalendarDays, HeartPulse, MoonStar, Scale, TrendingUp, Zap } from "lucide-react";
import { Card } from "../../components/ui/Card";
import { MetricTile } from "../../components/ui/MetricTile";
import { SectionHeader } from "../../components/ui/SectionHeader";
import { StatusBadge } from "../../components/ui/StatusBadge";
import { useAthleteProfile } from "../athlete-profile/AthleteProfileProvider";
import type { DailyHealthRecord } from "../health/dailyHealthTypes";
import type { AthleteIntelligenceSnapshot } from "../train/athleteIntelligence";
import type { AthleteTimelineEvent } from "../train/athleteTimeline";
import type { CompletedActivityRecord } from "../train/completedActivityHistory";
import { buildDailyCoachBriefing, type SignalCard } from "./dailyCoachBriefing";
import "./athleteCommandCenterPage.css";

type AthleteCommandCenterPageProps = {
  onGoToTrain: () => void;
  onOpenRideAnalysis: (activityId: string) => void;
  athleteIntelligence: AthleteIntelligenceSnapshot;
  activityHistoryRecords: CompletedActivityRecord[];
  dailyHealthRecords: DailyHealthRecord[];
  contextEvents: AthleteTimelineEvent[];
};

function signalIcon(signal: SignalCard["label"]) {
  if (signal === "Sleep") return <MoonStar size={18} />;
  if (signal === "Recovery") return <HeartPulse size={18} />;
  if (signal === "Fitness") return <TrendingUp size={18} />;
  if (signal === "Body weight") return <Scale size={18} />;
  return <Zap size={18} />;
}

function trendGlyph(trend: SignalCard["trend"]) {
  if (trend === "up") return <ArrowUp size={16} />;
  if (trend === "down") return <ArrowDown size={16} />;
  if (trend === "flat") return <ArrowRight size={16} />;
  return null;
}

function statusTone(status: string): "positive" | "attention" | "neutral" {
  if (status === "Excellent" || status === "Good") return "positive";
  if (status === "Moderate") return "attention";
  return "neutral";
}

export function AthleteCommandCenterPage({ onGoToTrain, onOpenRideAnalysis, athleteIntelligence, activityHistoryRecords, dailyHealthRecords, contextEvents }: AthleteCommandCenterPageProps) {
  const { athleteProfile } = useAthleteProfile();
  const briefing = useMemo(() => buildDailyCoachBriefing({
    athleteProfile,
    athleteIntelligence,
    activityHistoryRecords,
    dailyHealthRecords,
    contextEvents,
    preferredUnits: athleteProfile.personal.preferredUnits
  }), [activityHistoryRecords, athleteIntelligence, athleteProfile, contextEvents, dailyHealthRecords]);

  return (
    <div className="daily-briefing-layout">
      <Card className="daily-briefing-hero">
        <div className="daily-briefing-hero-header">
          <div>
            <p className="eyebrow">DAILY COACH BRIEFING</p>
            <h2>Good morning, {briefing.athleteName}</h2>
            <p className="ui-section-subtitle">Today's readiness and the next best action from your current ride, recovery, and context data.</p>
          </div>
          <div className="daily-readiness-block">
            <span>Today's readiness</span>
            <strong>{briefing.readinessScore}</strong>
            <StatusBadge label={briefing.readinessStatus} tone={statusTone(briefing.readinessStatus)} />
            <small>Confidence {briefing.confidence}</small>
          </div>
        </div>

        <div className="daily-recommendation-card">
          <div>
            <p className="eyebrow">PRIMARY RECOMMENDATION</p>
            <h3>{briefing.recommendation.kind}</h3>
            <p>{briefing.recommendation.explanation}</p>
          </div>
          <div className="daily-recommendation-meta">
            <div>
              <span>Estimated duration</span>
              <strong>{briefing.recommendation.estimatedDuration}</strong>
            </div>
            <div>
              <span>Confidence</span>
              <strong>{briefing.recommendation.confidence}</strong>
            </div>
          </div>
        </div>

        <div className="daily-briefing-actions">
          <button type="button" className="ride-analysis-button primary-action morning-primary-action" onClick={onGoToTrain}>Go To Train</button>
          <p className="command-note">{briefing.confidenceExplanation}</p>
        </div>
      </Card>

      <div className="daily-briefing-grid">
        <Card className="workflow-section">
          <SectionHeader eyebrow="WHY" title="Why This Recommendation" subtitle="Deterministic reasons from current inputs" />
          <ul className="command-list compact-list daily-briefing-list">
            {briefing.reasons.map(reason => (
              <li key={reason.id}><strong>{reason.label}</strong><span>{reason.source}</span></li>
            ))}
          </ul>
        </Card>

        <Card className="workflow-section">
          <SectionHeader eyebrow="CHECKLIST" title="Today's Checklist" subtitle="Actionable reminders from profile, context, and missing inputs" />
          {briefing.checklist.length === 0 ? (
            <p className="command-note">No deterministic checklist items were generated yet. Add more recovery, timeline, or ride data to expand today's reminders.</p>
          ) : (
            <ul className="command-list compact-list daily-briefing-list">
              {briefing.checklist.map(item => (
                <li key={item.id}><strong>{item.label}</strong><span>{item.source}</span></li>
              ))}
            </ul>
          )}
        </Card>

        <Card className="workflow-section">
          <SectionHeader eyebrow="UPCOMING EVENT" title="Upcoming Event" subtitle="Primary race countdown" action={<CalendarDays size={18} />} />
          {briefing.upcomingEvent.configured ? (
            <div className="daily-event-summary">
              <strong>{briefing.upcomingEvent.name}</strong>
              <span>{briefing.upcomingEvent.countdownLabel}</span>
              <small>{briefing.upcomingEvent.date ? new Date(briefing.upcomingEvent.date).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" }) : "Date unavailable"}</small>
            </div>
          ) : (
            <p className="command-note">No primary event configured.</p>
          )}
        </Card>

        <Card className="workflow-section daily-signals-card">
          <SectionHeader eyebrow="CURRENT SIGNALS" title="Current Signals" subtitle="Trend arrows only when enough history exists" />
          <div className="daily-signals-grid">
            {briefing.currentSignals.map(signal => (
              <article key={signal.id} className="daily-signal-item">
                <div>
                  {signalIcon(signal.label)}
                  <span>{signal.label}</span>
                </div>
                <strong>{signal.value}</strong>
                <small>{signal.detail}</small>
                <div className={`daily-signal-trend ${signal.trend}`}>{trendGlyph(signal.trend) ?? <span>Insufficient trend history.</span>}</div>
              </article>
            ))}
          </div>
        </Card>

        <Card className="workflow-section">
          <SectionHeader eyebrow="RECENT ACTIVITY" title="Recent Activity Summary" subtitle="Latest completed ride analysis" />
          {briefing.recentActivity.available ? (
            <>
              <div className="command-grid metrics-4">
                <MetricTile label="Ride" value={briefing.recentActivity.title} detail={briefing.recentActivity.rideType} />
                <MetricTile label="Distance" value={briefing.recentActivity.distance} detail="Latest completed ride" />
                <MetricTile label="Duration" value={briefing.recentActivity.duration} detail="Moving session time" />
                <MetricTile label="NP / IF" value={`${briefing.recentActivity.normalizedPower} / ${briefing.recentActivity.intensityFactor}`} detail="Persisted ride analysis" />
              </div>
              <div className="daily-briefing-actions compact">
                <button type="button" className="ride-analysis-button secondary-action" onClick={() => briefing.recentActivity.activityId && onOpenRideAnalysis(briefing.recentActivity.activityId)}>Open Ride Analysis</button>
              </div>
            </>
          ) : (
            <p className="command-note">{briefing.recentActivity.emptyMessage}</p>
          )}
        </Card>

        <Card className="workflow-section">
          <SectionHeader eyebrow="RECOVERY" title="Recovery Summary" subtitle="Latest Daily Health import" />
          {briefing.recoverySummary.available ? (
            <div className="command-grid metrics-3">
              {briefing.recoverySummary.items.map(item => (
                <MetricTile key={item.label} label={item.label} value={item.value} detail="Latest Daily Health" />
              ))}
            </div>
          ) : (
            <p className="command-note">{briefing.recoverySummary.emptyMessage}</p>
          )}
        </Card>
      </div>
    </div>
  );
}
